import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import jsPDF from 'jspdf';
import { useAuth } from '../context/AuthContext';
import {
  getClassroom,
  getSubmissions,
  getSubmissionPdf,
  deleteSubmission,
  markSubmissionsReviewed,
  updateSubmission,
} from '../services/api';
import { useI18n } from '../context/I18nContext';
import logo from '../images/image.png';

const PAGE_SIZE = 10;
const FETCH_LIMIT = 50;
const CHART_WIDTH = 150;
const CHART_HEIGHT = 70;
const CHART_BINS = 5;

const ClassroomSubmissionsPage = () => {
  const { classroomId } = useParams();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { t } = useI18n();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [classroom, setClassroom] = useState(null);
  const [submissions, setSubmissions] = useState([]);
  const [openFeedback, setOpenFeedback] = useState({});
  const [editingFeedback, setEditingFeedback] = useState({});
  const [editingGrade, setEditingGrade] = useState({});
  const [savingSubmissionId, setSavingSubmissionId] = useState(null);
  const [deletingSubmissionId, setDeletingSubmissionId] = useState(null);
  const [publishingAll, setPublishingAll] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [showStudentTrend, setShowStudentTrend] = useState(false);

  const fetchAllClassroomSubmissions = async (targetClassroomId) => {
    let page = 1;
    let allSubmissions = [];
    let totalCount = Infinity;

    while (allSubmissions.length < totalCount) {
      const response = await getSubmissions(undefined, targetClassroomId, {
        page,
        limit: FETCH_LIMIT,
      });
      const pageItems = response.data || [];
      const headerTotal = Number(response.headers?.['x-total-count']);

      if (Number.isFinite(headerTotal) && headerTotal >= 0) {
        totalCount = headerTotal;
      }

      allSubmissions = allSubmissions.concat(pageItems);

      if (pageItems.length === 0 || pageItems.length < FETCH_LIMIT) {
        break;
      }

      page += 1;
    }

    return allSubmissions;
  };

  const fetchData = async () => {
    try {
      setLoading(true);
      const [classroomRes, allSubmissions] =
        await Promise.all([
          getClassroom(classroomId),
          fetchAllClassroomSubmissions(classroomId),
        ]);

      setClassroom(classroomRes.data);
      setSubmissions(allSubmissions || []);
      setCurrentPage(1);
      setError('');
    } catch (err) {
      setError(err.response?.data?.message || t('errors.failedLoadSubmissions'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (classroomId) {
      fetchData();
    }
  }, [classroomId]);

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const sortedSubmissions = useMemo(
    () =>
      [...submissions].sort(
        (a, b) => new Date(b.submittedAt) - new Date(a.submittedAt)
      ),
    [submissions]
  );

  const totalPages = Math.max(1, Math.ceil(sortedSubmissions.length / PAGE_SIZE));

  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages]);

  const paginatedSubmissions = useMemo(() => {
    const start = (currentPage - 1) * PAGE_SIZE;
    return sortedSubmissions.slice(start, start + PAGE_SIZE);
  }, [sortedSubmissions, currentPage]);

  const groupedSubmissions = useMemo(() => {
    const grouped = {};
    paginatedSubmissions.forEach((s) => {
      const assignmentTitle = s.assignmentId?.title || 'Assignment';
      if (!grouped[assignmentTitle]) grouped[assignmentTitle] = [];
      grouped[assignmentTitle].push(s);
    });
    return grouped;
  }, [paginatedSubmissions]);

  const assignmentReports = useMemo(() => {
    const grouped = {};

    submissions.forEach((submission) => {
      const assignment = submission.assignmentId || {};
      const assignmentId = assignment._id || 'unknown-assignment';
      const assignmentTitle = assignment.title || 'Assignment';
      const totalPoints = Number(assignment.totalPoints || 0);
      const studentKey =
        submission.studentId?._id ||
        `name:${String(submission.studentName || 'Student').trim().toLowerCase()}`;
      const studentName = submission.studentId?.name || submission.studentName || 'Student';
      const submittedAt = submission.submittedAt ? new Date(submission.submittedAt) : new Date(0);

      if (!grouped[assignmentId]) {
        grouped[assignmentId] = {
          assignmentId,
          title: assignmentTitle,
          totalPoints,
          students: {},
        };
      }

      const currentEntry = grouped[assignmentId].students[studentKey];
      if (!currentEntry || submittedAt > currentEntry.submittedAt) {
        grouped[assignmentId].students[studentKey] = {
          studentName,
          email: submission.studentId?.email || '',
          grade:
            typeof submission.grade === 'number' && Number.isFinite(submission.grade)
              ? submission.grade
              : null,
          submittedAt,
        };
      }
    });

    return Object.values(grouped)
      .map((group) => {
        const rows = Object.values(group.students).sort((a, b) =>
          a.studentName.localeCompare(b.studentName)
        );
        const gradedRows = rows.filter((row) => typeof row.grade === 'number');
        const grades = gradedRows.map((row) => row.grade);
        const average =
          grades.length > 0
            ? grades.reduce((sum, value) => sum + value, 0) / grades.length
            : null;
        const highest = grades.length > 0 ? Math.max(...grades) : null;
        const lowest = grades.length > 0 ? Math.min(...grades) : null;

        return {
          assignmentId: group.assignmentId,
          title: group.title,
          totalPoints: group.totalPoints,
          rows,
          grades,
          average,
          highest,
          lowest,
        };
      })
      .sort((a, b) => a.title.localeCompare(b.title));
  }, [submissions]);

  const studentProgress = useMemo(() => {
    const grouped = {};
    submissions.forEach((s) => {
      const name = s.studentId?.name || s.studentName || 'Student';
      const key = s.studentId?._id || `${name}-${s._id}`;
      if (!grouped[key]) {
        grouped[key] = {
          name,
          grades: [],
        };
      }
      if (typeof s.grade === 'number') {
        grouped[key].grades.push({
          value: s.grade,
          submittedAt: s.submittedAt,
        });
      }
    });

    return Object.values(grouped)
      .map((student) => {
        const sorted = student.grades.sort(
          (a, b) => new Date(a.submittedAt) - new Date(b.submittedAt)
        );
        const grades = sorted.map((g) => g.value);
        const average =
          grades.length > 0
            ? Math.round(grades.reduce((sum, v) => sum + v, 0) / grades.length)
            : null;
        const latest = grades.length > 0 ? grades[grades.length - 1] : null;
        return {
          name: student.name,
          grades,
          average,
          latest,
        };
      })
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [submissions]);

  const buildSparkline = (grades) => {
    if (!grades || grades.length === 0) return '';
    const max = Math.max(...grades, 1);
    const min = Math.min(...grades, 0);
    const span = Math.max(max - min, 1);
    const points = grades.map((g, index) => {
      const x = (index / Math.max(grades.length - 1, 1)) * 100;
      const y = 24 - ((g - min) / span) * 24;
      return `${x},${y}`;
    });
    return `M ${points.join(' L ')}`;
  };

  const buildHistogram = (grades, totalPoints) => {
    const safeMax = Math.max(Number(totalPoints) || 0, ...grades, 1);
    const bins = Array.from({ length: CHART_BINS }, (_, index) => ({
      labelStart: (safeMax / CHART_BINS) * index,
      labelEnd: (safeMax / CHART_BINS) * (index + 1),
      count: 0,
    }));

    grades.forEach((grade) => {
      const rawIndex = Math.floor((grade / safeMax) * CHART_BINS);
      const binIndex = Math.min(CHART_BINS - 1, Math.max(0, rawIndex));
      bins[binIndex].count += 1;
    });

    return bins;
  };

  const exportAssignmentReport = (report) => {
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const margin = 16;
    let cursorY = 18;

    const rows = report.rows || [];
    const gradedRows = rows.filter((row) => typeof row.grade === 'number');
    const histogram = buildHistogram(report.grades, report.totalPoints);
    const maxCount = Math.max(1, ...histogram.map((bin) => bin.count));
    const chartX = margin;
    const chartY = 58;

    doc.setFillColor(15, 118, 110);
    doc.roundedRect(margin, 12, pageWidth - margin * 2, 22, 4, 4, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(18);
    doc.text('Assignment Grade Report', margin + 6, 24);
    doc.setFontSize(10);
    doc.text(classroom?.name || 'Classroom', margin + 6, 30);

    doc.setTextColor(15, 23, 42);
    cursorY = 44;
    doc.setFontSize(14);
    doc.text(report.title || 'Assignment', margin, cursorY);
    cursorY += 7;
    doc.setFontSize(10);
    doc.text(`Total points: ${report.totalPoints || 0}`, margin, cursorY);
    doc.text(`Students: ${rows.length}`, margin + 55, cursorY);
    doc.text(`Graded: ${gradedRows.length}`, margin + 90, cursorY);
    cursorY += 8;
    doc.text(
      `Average: ${report.average != null ? report.average.toFixed(2) : 'N/A'}`,
      margin,
      cursorY
    );
    doc.text(`Highest: ${report.highest ?? 'N/A'}`, margin + 55, cursorY);
    doc.text(`Lowest: ${report.lowest ?? 'N/A'}`, margin + 90, cursorY);

    doc.setFontSize(12);
    doc.text('Grade Distribution', chartX, chartY - 6);
    doc.setDrawColor(203, 213, 225);
    doc.rect(chartX, chartY, CHART_WIDTH, CHART_HEIGHT);

    const barWidth = CHART_WIDTH / histogram.length - 6;
    histogram.forEach((bin, index) => {
      const barHeight = (bin.count / maxCount) * (CHART_HEIGHT - 18);
      const x = chartX + 6 + index * (barWidth + 6);
      const y = chartY + CHART_HEIGHT - barHeight - 10;
      doc.setFillColor(16, 185, 129);
      doc.rect(x, y, barWidth, barHeight, 'F');
      doc.setFontSize(8);
      doc.setTextColor(71, 85, 105);
      doc.text(String(bin.count), x + barWidth / 2, y - 2, { align: 'center' });
      const label = `${Math.round(bin.labelStart)}-${Math.round(bin.labelEnd)}`;
      doc.text(label, x + barWidth / 2, chartY + CHART_HEIGHT - 3, { align: 'center' });
    });

    cursorY = chartY + CHART_HEIGHT + 16;
    doc.setTextColor(15, 23, 42);
    doc.setFontSize(12);
    doc.text('Student Marks', margin, cursorY);
    cursorY += 8;

    const drawTableHeader = () => {
      doc.setFillColor(241, 245, 249);
      doc.rect(margin, cursorY, pageWidth - margin * 2, 8, 'F');
      doc.setFontSize(9);
      doc.setTextColor(15, 23, 42);
      doc.text('Student', margin + 2, cursorY + 5.5);
      doc.text('Email', margin + 70, cursorY + 5.5);
      doc.text('Grade', pageWidth - 38, cursorY + 5.5);
      cursorY += 10;
    };

    drawTableHeader();

    rows.forEach((row) => {
      if (cursorY > pageHeight - 18) {
        doc.addPage();
        cursorY = 18;
        drawTableHeader();
      }

      doc.setFontSize(9);
      doc.setTextColor(30, 41, 59);
      const studentName = doc.splitTextToSize(row.studentName || 'Student', 60);
      const email = doc.splitTextToSize(row.email || '-', 55);
      const lineHeight = Math.max(studentName.length, email.length, 1) * 5;
      doc.text(studentName, margin + 2, cursorY + 4);
      doc.text(email, margin + 70, cursorY + 4);
      doc.text(
        row.grade != null ? `${row.grade}/${report.totalPoints || 0}` : 'N/A',
        pageWidth - 38,
        cursorY + 4
      );
      doc.setDrawColor(226, 232, 240);
      doc.line(margin, cursorY + lineHeight + 1, pageWidth - margin, cursorY + lineHeight + 1);
      cursorY += lineHeight + 4;
    });

    const safeTitle = String(report.title || 'assignment-report')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-|-$/g, '');
    doc.save(`${safeTitle || 'assignment-report'}.pdf`);
  };

  const toggleFeedback = (id) => {
    setOpenFeedback((prev) => ({
      ...prev,
      [id]: !prev[id],
    }));
  };

  const handleFeedbackChange = (id, value) => {
    setEditingFeedback((prev) => ({
      ...prev,
      [id]: value,
    }));
  };

  const handleGradeChange = (id, value) => {
    setEditingGrade((prev) => ({
      ...prev,
      [id]: value,
    }));
  };

  const handleSaveReview = async (submission) => {
    const nextFeedback = Object.prototype.hasOwnProperty.call(editingFeedback, submission._id)
      ? editingFeedback[submission._id]
      : submission.feedback || '';
    const nextGradeRaw = Object.prototype.hasOwnProperty.call(editingGrade, submission._id)
      ? editingGrade[submission._id]
      : submission.grade;

    const payload = { feedback: nextFeedback };
    if (nextGradeRaw !== '' && nextGradeRaw != null) {
      payload.grade = Number(nextGradeRaw);
    }

    try {
      setSavingSubmissionId(submission._id);
      const response = await updateSubmission(submission._id, payload);
      const updatedSubmission = response.data;
      setSubmissions((prev) =>
        prev.map((item) =>
          item._id === submission._id ? { ...item, ...updatedSubmission } : item
        )
      );
      setEditingFeedback((prev) => {
        const next = { ...prev };
        delete next[submission._id];
        return next;
      });
      setEditingGrade((prev) => {
        const next = { ...prev };
        delete next[submission._id];
        return next;
      });
      setError('');
    } catch (err) {
      setError(err.response?.data?.message || t('errors.failedSubmitAssignment'));
    } finally {
      setSavingSubmissionId(null);
    }
  };

  const handlePublishAllReviewed = async () => {
    try {
      setPublishingAll(true);
      await markSubmissionsReviewed({ classroomId });
      await fetchData();
      setError('');
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to publish reviewed submissions');
    } finally {
      setPublishingAll(false);
    }
  };

  const handleViewPdf = async (submissionId) => {
    try {
      const response = await getSubmissionPdf(submissionId);
      const blobUrl = window.URL.createObjectURL(response.data);
      window.open(blobUrl, '_blank', 'noopener,noreferrer');
      setTimeout(() => {
        window.URL.revokeObjectURL(blobUrl);
      }, 60000);
      setError('');
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to open PDF');
    }
  };

  const handleDeleteSubmission = async (submissionId) => {
    const confirmed = window.confirm('Delete this submission?');
    if (!confirmed) return;

    try {
      setDeletingSubmissionId(submissionId);
      await deleteSubmission(submissionId);
      setSubmissions((prev) => prev.filter((item) => item._id !== submissionId));
      setOpenFeedback((prev) => {
        const next = { ...prev };
        delete next[submissionId];
        return next;
      });
      setEditingFeedback((prev) => {
        const next = { ...prev };
        delete next[submissionId];
        return next;
      });
      setEditingGrade((prev) => {
        const next = { ...prev };
        delete next[submissionId];
        return next;
      });
      setError('');
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to delete submission');
    } finally {
      setDeletingSubmissionId(null);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <nav className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 py-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate(-1)}
              className="rounded-lg border border-slate-200 px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"
            >
              ← {t('common.back')}
            </button>
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.25em] text-emerald-700">
                Sa7a7ly
              </p>
              <h1 className="text-2xl font-bold text-slate-900">
                {t('common.submissions')}
              </h1>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <span className="rounded-full bg-slate-100 px-3 py-1 text-sm text-slate-600">
              {user?.name}
            </span>
            <button
              onClick={handleLogout}
              className="px-4 py-2 bg-slate-900 text-white rounded-lg hover:bg-slate-800 transition"
            >
              {t('common.logout')}
            </button>
          </div>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto px-4 py-8 space-y-8">
        <div className="relative overflow-hidden rounded-2xl border border-slate-200 bg-gradient-to-br from-slate-50 via-white to-emerald-50 p-6 shadow-sm">
          <div className="absolute -right-10 -top-10 h-36 w-36 rounded-full bg-emerald-200/40 blur-2xl" />
          <div className="absolute -left-12 -bottom-12 h-40 w-40 rounded-full bg-sky-200/40 blur-2xl" />
          <div className="relative flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
            <div className="max-w-2xl">
              <div className="flex items-center gap-3">
                <img
                  src={logo}
                  alt="Sa7a7ly logo"
                  className="h-11 w-11 rounded-xl bg-white p-2 shadow"
                />
                <div>
                  <p className="text-xs font-semibold uppercase tracking-[0.25em] text-emerald-700">
                    {classroom?.name || t('common.classroom')}
                  </p>
                  <h2 className="text-3xl font-bold text-slate-900">
                    {t('common.submissions')}
                  </h2>
                </div>
              </div>
              <p className="mt-3 text-slate-700">
                {t('resubmissions.review')}
              </p>
              <button
                type="button"
                onClick={handlePublishAllReviewed}
                disabled={publishingAll || submissions.length === 0}
                className="mt-4 rounded-lg bg-emerald-600 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-700 disabled:opacity-50"
              >
                {publishingAll ? t('common.loading') : 'Publish all as reviewed'}
              </button>
            </div>
            <div className="rounded-2xl border border-slate-200 bg-white/80 px-6 py-4 text-center shadow-sm">
              <p className="text-2xl font-bold text-slate-900">
                {submissions.length}
              </p>
              <p className="text-sm text-slate-600">{t('common.submissions')}</p>
            </div>
          </div>
        </div>

        {error && (
          <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-red-700">
            {error}
          </div>
        )}

        {loading ? (
          <div className="text-center py-10 text-slate-600">
            {t('common.loading')}
          </div>
        ) : submissions.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-slate-200 bg-white p-8 text-center text-slate-600">
            {t('common.noSubmissionsFound')}
          </div>
        ) : (
          <div className="space-y-6">
            <section className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
              <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                <div>
                  <p className="text-xs font-semibold uppercase tracking-[0.25em] text-slate-500">
                    Progress
                  </p>
                  <h3 className="text-2xl font-bold text-slate-900">
                    Student feedback trend
                  </h3>
                </div>
                <span className="rounded-full bg-slate-100 px-3 py-1 text-sm text-slate-600">
                  {studentProgress.length} students
                </span>
              </div>
              <button
                type="button"
                onClick={() => setShowStudentTrend((prev) => !prev)}
                className="mt-4 rounded-lg border border-slate-200 px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"
              >
                {showStudentTrend ? 'Hide trend' : 'View trend'}
              </button>
              {showStudentTrend && (
                <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-2">
                  {studentProgress.map((student) => (
                    <div
                      key={student.name}
                      className="rounded-xl border border-slate-200 bg-slate-50 p-4"
                    >
                      <div className="flex items-center justify-between">
                        <p className="text-sm font-semibold text-slate-900">
                          {student.name}
                        </p>
                        <span className="text-xs font-semibold text-emerald-700">
                          Avg: {student.average ?? 'N/A'}
                        </span>
                      </div>
                      <div className="mt-2 flex items-center justify-between">
                        <p className="text-xs text-slate-500">
                          Latest: {student.latest ?? 'N/A'}
                        </p>
                        <svg viewBox="0 0 100 24" className="h-6 w-28">
                          <path
                            d={buildSparkline(student.grades)}
                            fill="none"
                            stroke="#10b981"
                            strokeWidth="2"
                          />
                        </svg>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </section>

            <section className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
              <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                <div>
                  <p className="text-xs font-semibold uppercase tracking-[0.25em] text-slate-500">
                    Reports
                  </p>
                  <h3 className="text-2xl font-bold text-slate-900">
                    Assignment grade reports
                  </h3>
                </div>
                <span className="rounded-full bg-slate-100 px-3 py-1 text-sm text-slate-600">
                  {assignmentReports.length} assignments
                </span>
              </div>
              <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-2">
                {assignmentReports.map((report) => (
                  <div
                    key={report.assignmentId}
                    className="rounded-xl border border-slate-200 bg-slate-50 p-4"
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <p className="text-sm font-semibold text-slate-900">{report.title}</p>
                        <p className="mt-1 text-xs text-slate-500">
                          {report.rows.length} students, average{' '}
                          {report.average != null ? report.average.toFixed(2) : 'N/A'}
                        </p>
                      </div>
                      <button
                        type="button"
                        onClick={() => exportAssignmentReport(report)}
                        className="rounded-lg bg-emerald-600 px-3 py-2 text-xs font-semibold text-white hover:bg-emerald-700"
                      >
                        Export PDF
                      </button>
                    </div>
                    <div className="mt-3 flex items-center gap-2 text-xs text-slate-600">
                      <span className="rounded-full bg-white px-3 py-1">
                        Highest: {report.highest ?? 'N/A'}
                      </span>
                      <span className="rounded-full bg-white px-3 py-1">
                        Lowest: {report.lowest ?? 'N/A'}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </section>

            {Object.keys(groupedSubmissions).map((title) => (
              <div
                key={title}
                className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm"
              >
                <h3 className="text-xl font-bold text-slate-900">{title}</h3>
                <div className="mt-4 space-y-3">
                  {groupedSubmissions[title].map((submission) => (
                    <div
                      key={submission._id}
                      className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-3"
                    >
                      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                        <div>
                          <p className="text-sm font-semibold text-slate-900">
                            {submission.studentId?.name || submission.studentName || 'Student'}
                          </p>
                          <p className="text-xs text-slate-600">
                            {new Date(submission.submittedAt).toLocaleString()}
                          </p>
                        </div>
                        <span className="text-xs font-semibold bg-emerald-100 text-emerald-700 px-3 py-1 rounded-full">
                          {t('submit.grade')}: {submission.grade ?? 'N/A'}
                        </span>
                        <span
                          className={`text-xs font-semibold px-3 py-1 rounded-full ${
                            submission.reviewedByStaffAt
                              ? 'bg-sky-100 text-sky-700'
                              : 'bg-amber-100 text-amber-700'
                          }`}
                        >
                          {submission.reviewedByStaffAt ? 'Reviewed' : 'Pending review'}
                        </span>
                      </div>
                      <button
                        type="button"
                        onClick={() => toggleFeedback(submission._id)}
                        className="mt-3 rounded-lg border border-slate-200 px-3 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-100"
                      >
                        {openFeedback[submission._id]
                          ? t('common.close')
                          : t('submit.feedback')}
                      </button>
                      {submission.pdfPath && (
                        <button
                          type="button"
                          onClick={() => handleViewPdf(submission._id)}
                          className="mt-3 ml-2 inline-flex rounded-lg border border-slate-200 px-3 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-100"
                        >
                          {t('common.viewPdf')}
                        </button>
                      )}
                      <button
                        type="button"
                        onClick={() => handleDeleteSubmission(submission._id)}
                        disabled={deletingSubmissionId === submission._id}
                        className="mt-3 ml-2 inline-flex rounded-lg border border-red-200 px-3 py-2 text-xs font-semibold text-red-700 hover:bg-red-50 disabled:opacity-50"
                      >
                        {deletingSubmissionId === submission._id ? t('common.loading') : 'Delete'}
                      </button>
                      {openFeedback[submission._id] && (
                        <div className="mt-3 space-y-3">
                          <div>
                            <p className="mb-1 text-xs font-semibold uppercase tracking-[0.12em] text-slate-500">
                              {t('submit.grade')}
                            </p>
                            <input
                              type="number"
                              value={
                                Object.prototype.hasOwnProperty.call(editingGrade, submission._id)
                                  ? editingGrade[submission._id]
                                  : submission.grade ?? ''
                              }
                              onChange={(e) =>
                                handleGradeChange(submission._id, e.target.value)
                              }
                              className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-emerald-500"
                            />
                          </div>

                          <div>
                            <p className="mb-1 text-xs font-semibold uppercase tracking-[0.12em] text-slate-500">
                              {t('submit.feedback')}
                            </p>
                            <textarea
                              rows={8}
                              value={
                                Object.prototype.hasOwnProperty.call(editingFeedback, submission._id)
                                  ? editingFeedback[submission._id]
                                  : submission.feedback || ''
                              }
                              onChange={(e) =>
                                handleFeedbackChange(submission._id, e.target.value)
                              }
                              className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-emerald-500"
                            />
                          </div>

                          <button
                            type="button"
                            disabled={savingSubmissionId === submission._id}
                            onClick={() => handleSaveReview(submission)}
                            className="rounded-lg bg-emerald-600 px-4 py-2 text-xs font-semibold text-white hover:bg-emerald-700 disabled:opacity-50"
                          >
                            {savingSubmissionId === submission._id
                              ? t('common.loading')
                              : t('common.update')}
                          </button>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            ))}

            <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
              <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <p className="text-sm text-slate-600">
                  Showing {(currentPage - 1) * PAGE_SIZE + 1}-
                  {Math.min(currentPage * PAGE_SIZE, sortedSubmissions.length)} of{' '}
                  {sortedSubmissions.length} submissions
                </p>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                    disabled={currentPage === 1}
                    className="rounded-lg border border-slate-200 px-3 py-1.5 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50"
                  >
                    Previous
                  </button>
                  <span className="rounded-lg bg-slate-100 px-3 py-1.5 text-sm font-semibold text-slate-700">
                    {currentPage} / {totalPages}
                  </span>
                  <button
                    type="button"
                    onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                    disabled={currentPage === totalPages}
                    className="rounded-lg border border-slate-200 px-3 py-1.5 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50"
                  >
                    Next
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ClassroomSubmissionsPage;
